import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { ROLE_LABELS } from "../lib/api";
import { LayoutDashboard, ClipboardList, ListChecks, FileSpreadsheet, Building2, Users as UsersIcon, Bell, LogOut, Leaf, ShieldCheck, BarChart3, CalendarDays, Sparkles, Menu, X, Newspaper, Gauge } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: null },
  { to: "/audits", label: "Audits", icon: ClipboardList, roles: null },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, roles: null },
  { to: "/actions", label: "Corrective Actions", icon: ListChecks, roles: null },
  { to: "/progress", label: "Hotel Progress", icon: Gauge, roles: ["corporate_admin", "regional_head", "hotel_gm"] },
  { to: "/templates", label: "Audit Templates", icon: FileSpreadsheet, roles: ["corporate_admin"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["corporate_admin", "regional_head", "hotel_gm"] },
  { to: "/digest", label: "Monthly Digest", icon: Newspaper, roles: ["corporate_admin", "regional_head", "hotel_gm"] },
  { to: "/ai-insights", label: "AI Insights", icon: Sparkles, roles: ["corporate_admin", "regional_head"] },
  { to: "/hotels", label: "Hotels", icon: Building2, roles: ["corporate_admin", "regional_head", "hotel_gm"] },
  { to: "/users", label: "Users", icon: UsersIcon, roles: ["corporate_admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = useMemo(() => NAV.filter((n) => !n.roles || n.roles.includes(user.role)), [user.role]);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (e) {
      console.error("Failed to load notifications", e);
    }
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  const markRead = async () => {
    if (unread) {
      await api.put("/notifications/read");
      loadNotifs();
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FBFBF9]">
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" data-testid="mobile-nav-backdrop" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={`w-64 shrink-0 bg-[#0F2E1B] text-white flex flex-col fixed inset-y-0 z-50 transform transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-white/10">
          <div className="w-9 h-9 rounded-lg bg-emerald-400/20 flex items-center justify-center">
            <Leaf className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-sm tracking-tight" style={{ fontFamily: "Manrope" }}>Fern Hotels</p>
            <p className="text-[11px] text-white/50 uppercase tracking-widest">Audit Platform</p>
          </div>
          <button className="lg:hidden p-1.5 text-white/60 hover:text-white" data-testid="mobile-nav-close" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setMobileOpen(false)}
              data-testid={`sidebar-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-white/10 text-white font-medium" : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-4 border-t border-white/10 text-xs text-white/40">
          The Fern Hotels &amp; Resorts
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#E7E5DF] px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-stone-100 text-stone-700" data-testid="mobile-nav-toggle" onClick={() => setMobileOpen(true)}>
              <Menu className="w-5 h-5" />
            </button>
            <p className="text-sm text-stone-500">
              <span className="font-medium text-stone-800">{ROLE_LABELS[user.role]}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 lg:gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="notifications-bell" onClick={markRead} className="relative p-2 rounded-lg hover:bg-stone-100 transition-colors">
                  <Bell className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[10px] bg-rose-600 text-white rounded-full flex items-center justify-center">{unread}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[min(24rem,92vw)] p-0 max-h-96 overflow-y-auto">
                <div className="px-4 py-3 border-b font-semibold text-sm">Notifications</div>
                {notifs.length === 0 && <p className="px-4 py-6 text-sm text-stone-500">No notifications yet.</p>}
                {notifs.map((n) => (
                  <div key={n.id} className="px-4 py-3 border-b last:border-0 hover:bg-stone-50 cursor-pointer" onClick={() => n.link && navigate(n.link)}>
                    <p className="text-sm font-medium text-stone-800">{n.title}</p>
                    <p className="text-xs text-stone-500 mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-stone-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-lg hover:bg-stone-100 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#14532D] text-white text-xs font-bold flex items-center justify-center">
                    {user.name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-stone-800">{user.name}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72">
                <p className="font-semibold text-sm">{user.name}</p>
                <p className="text-xs text-stone-500">{user.email}</p>
                <div className="flex items-center justify-between mt-4 py-2 border-t">
                  <div className="flex items-center gap-2 text-sm text-stone-700">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" strokeWidth={1.5} /> Two-factor auth
                  </div>
                  <span data-testid="mfa-enforced-badge" className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">Always on</span>
                </div>
                <button
                  data-testid="logout-button"
                  onClick={async () => { await logout(); navigate("/login"); }}
                  className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8 fade-in">{children}</main>
      </div>
    </div>
  );
}
